import React, { useCallback, useState } from 'react';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { PORT_RADIUS } from './ellipsis.tsx';
import { EDGE_TYPE } from './directionKeys.tsx';
import { type Point, type TransitionIcons, type ValidationFlag, PortIndex } from './toPortInfo.tsx';
import type { ItemReference } from './ListEdgeDetails.tsx';
import { DiagramArrow } from './DiagramArrow.tsx';
import TransitionLabel from './TransitionLabel.tsx';
import { truncateLabel } from './isEveryPropertyDefined.tsx';
import { useStrokeColor } from './useStrokeColor.tsx';
import {
	LOZENGE_BORDER_RADIUS,
	LOZENGE_HEIGHT,
	getArrowPosition,
	getLabelPosition,
	getLozengePosition,
	getLozengeTextPosition,
	getLozengeWidth,
	globalLoopedTransitionRoute,
} from './getLozengePosition.tsx';
import messages from './GlobalLoopedTransition.messages.tsx';

export const GLOBAL_LOOP_LABEL_MAX_LENGTH = 13; // as per Workflow Designer

export interface Props {
	id: string;
	flag?: ValidationFlag;
	isHovered?: boolean;
	isImmutable?: boolean;
	isSelected?: boolean;
	isTargeted?: boolean;
	position: Point | undefined;
	label: string;
	onSelect?: (item: ItemReference | undefined) => void;
	onHover?: (item: ItemReference | undefined) => void;
	icons?: TransitionIcons[];
}

export const GlobalLoopedTransition = ({
	id,
	flag,
	isHovered = false,
	isImmutable,
	position: containerPosition,
	isSelected = false,
	isTargeted = false,
	label,
	onSelect,
	onHover,
	icons = [],
}: Props) => {
	const { formatMessage } = useIntl();
	const stroke = useStrokeColor({ flag, isHighlighted: false, isSelected, isHovered });
	const [textRef, setTextRef] = useState<SVGTextElement | null>(null);
	const lozengeText = truncateLabel(formatMessage(messages.anyLozengeTextNonFinal));
	const textWidth = textRef?.getBBox?.().width || 0;

	const lozengeWidth = getLozengeWidth(textWidth);

	const onGroupClick = useCallback(() => {
		if (!isImmutable) {
			onSelect?.({ type: EDGE_TYPE, id });
		}
	}, [id, isImmutable, onSelect]);

	const onMouseEnter = useCallback(() => {
		onHover?.({ id, type: EDGE_TYPE });
	}, [id, onHover]);

	const onMouseLeave = useCallback(() => {
		onHover?.(undefined);
	}, [onHover]);

	const onTextRef = useCallback((ref: SVGTextElement | null) => {
		if (ref) {
			setTextRef(ref);
		}
	}, []);

	if (!containerPosition) {
		return null;
	}

	const lozengePosition = getLozengePosition(containerPosition);
	const lozengeTextPosition = getLozengeTextPosition(lozengePosition, lozengeWidth);
	const path = globalLoopedTransitionRoute(lozengePosition, lozengeWidth);
	const arrowPosition = getArrowPosition(lozengePosition, lozengeWidth);
	const labelPosition = getLabelPosition(containerPosition);

	return (
		<g onClick={onGroupClick} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
			{/* Click and hover area */}
			<path
				data-testid="accessible-workflow-diagram.interactable-path"
				fill="none"
				stroke="transparent"
				d={path}
				strokeWidth="15"
			/>
			{/* Transition line */}
			<path
				data-testid="accessible-workflow-diagram.visible-path"
				fill="none"
				stroke={stroke}
				strokeLinejoin="round"
				d={path}
				strokeWidth="2"
			/>
			<DiagramArrow
				portPosition={arrowPosition}
				stroke={stroke}
				portIndex={PortIndex.Top}
				portOffset={PORT_RADIUS}
			/>
			{/* Lozenge bg */}
			<rect
				{...lozengePosition}
				width={lozengeWidth}
				height={LOZENGE_HEIGHT}
				rx={LOZENGE_BORDER_RADIUS}
				ry={LOZENGE_BORDER_RADIUS}
				fill={token('color.background.neutral.bold')}
			/>
			{/* Lozenge text */}
			<text
				{...lozengeTextPosition}
				ref={onTextRef}
				textAnchor="middle"
				fontSize="11px"
				fill={token('color.text.inverse')}
			>
				<tspan>{lozengeText}</tspan>
			</text>
			<TransitionLabel
				id={id}
				position={labelPosition}
				label={label}
				flag={flag}
				icons={icons}
				isTargeted={isTargeted}
				isSelected={isSelected}
				isHovered={isHovered}
				isImmutable={isImmutable}
				maxLabelLength={GLOBAL_LOOP_LABEL_MAX_LENGTH}
				onSelect={onSelect}
			/>
		</g>
	);
};
